import { type ChatStatus, type UIMessage, getToolName, isToolUIPart } from "ai";
import { useEffect, useRef } from "react";
import { eventBus } from "$root/event-bus";

const LAB_TOOL_PREFIX = "portfolio_lab-";

const isTurnStreaming = (status: ChatStatus) => status === "submitted" || status === "streaming";

/**
 * Tell the Portfolio Lab each time a bot write to it returns in this tab's live stream (MDOT-25921),
 * as `bot-lab-write-result`, so it re-reads its columns.
 *
 * The app-wide refresh (`botWriteInvalidation.ts`) is keyed to the tool CALL and debounced, so a lab
 * write slower than the debounce is read before it lands. The result is the first moment this tab
 * knows the server holds the write; an errored result counts too, since the save may have gone
 * through before the tool failed.
 *
 * A result is live when it first appears while a turn of this tab is streaming, or in the render that
 * ends it. Results that appear while the chat is idle (a restored conversation, the poll of a stored
 * one) are history, which the Lab already loaded. Each tool call is told once. The tools are the
 * Lab's writes as the call-time refresh counts them: mutating, and not a background task.
 */
export function useBotLabWriteResults({
	messages,
	status,
	mutatingToolNames,
	taskToolNames,
}: {
	messages: UIMessage[];
	status: ChatStatus;
	mutatingToolNames: ReadonlySet<string>;
	taskToolNames: ReadonlySet<string>;
}): void {
	const seenToolCallIds = useRef(new Set<string>());
	const previousStatus = useRef(status);

	useEffect(() => {
		const live = isTurnStreaming(status) || isTurnStreaming(previousStatus.current);
		previousStatus.current = status;
		for (const message of messages) {
			if (message.role !== "assistant") continue;
			for (const part of message.parts) {
				if (!isToolUIPart(part) || (part.state !== "output-available" && part.state !== "output-error")) continue;
				if (seenToolCallIds.current.has(part.toolCallId)) continue;
				seenToolCallIds.current.add(part.toolCallId);
				const toolName = getToolName(part);
				if (
					live &&
					toolName.startsWith(LAB_TOOL_PREFIX) &&
					mutatingToolNames.has(toolName) &&
					!taskToolNames.has(toolName)
				) {
					eventBus.emit("bot-lab-write-result", { toolName });
				}
			}
		}
	}, [messages, status, mutatingToolNames, taskToolNames]);
}
